// src/components/ui/Loader/LoaderUi.tsx
// ════════════════════════════════════════════════════════════
//  Page-level loading skeleton — used as Suspense fallback
//  when lazy-loaded routes are being fetched.
//
//  Usage:
//    <Suspense fallback={<LoaderUi />}>
//      <LazyPage />
//    </Suspense>
// ════════════════════════════════════════════════════════════

import React from 'react';
import { motion } from 'framer-motion';
import { useThemeContext } from '../../../context/ThemeContext';

const LoaderUi: React.FC = () => {
  const { isDark } = useThemeContext();

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-8 backdrop-blur-xl"
      style={{ background: isDark ? 'rgba(7, 11, 20, 0.85)' : 'rgba(255, 255, 255, 0.85)' }}
    >
      {/* Glow Orbs in Background */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] glow-orb glow-blue pointer-events-none blur-3xl transition-opacity duration-300 ${isDark ? 'opacity-20' : 'opacity-5'}`} />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] h-[250px] glow-orb glow-violet pointer-events-none blur-3xl delay-1000 transition-opacity duration-300 ${isDark ? 'opacity-15' : 'opacity-5'}`} />

      {/* Main Loader Container */}
      <div className="relative flex items-center justify-center w-32 h-32">
        {/* Animated Gradient Circular Ring */}
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="64"
            cy="64"
            r="50"
            className="stroke-white/5 dark:stroke-white/5"
            strokeWidth="3"
            fill="transparent"
          />
          <motion.circle
            cx="64"
            cy="64"
            r="50"
            stroke="url(#loaderGradient)"
            strokeWidth="4"
            fill="transparent"
            strokeDasharray="314"
            initial={{ strokeDashoffset: 314 }}
            animate={{ 
              strokeDashoffset: [314, 100, 314],
              rotate: [0, 360]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            strokeLinecap="round"
            style={{ originX: "64px", originY: "64px" }}
          />
          <defs>
            <linearGradient id="loaderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--blue)" />
              <stop offset="50%" stopColor="var(--violet)" />
              <stop offset="100%" stopColor="var(--cyan)" />
            </linearGradient>
          </defs>
        </svg>

        {/* Pulsing Central Logo Box */}
        <motion.div
          className="absolute w-20 h-20 rounded-2xl flex items-center justify-center font-display font-black text-2xl tracking-wider select-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 0 12px rgba(255,255,255,0.05)',
            color: 'var(--t1)'
          }}
          animate={{
            scale: [0.95, 1.05, 0.95],
            boxShadow: [
              '0 8px 32px 0 rgba(15, 98, 254, 0.15)',
              '0 8px 32px 0 rgba(139, 92, 246, 0.25)',
              '0 8px 32px 0 rgba(15, 98, 254, 0.15)'
            ]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[var(--blue)] to-[var(--violet)]">
            FS
          </span>
        </motion.div>
      </div>

      {/* Progress & Brand Subtext */}
      <div className="flex flex-col items-center gap-2">
        <motion.div 
          className="font-bold text-sm tracking-widest text-center uppercase"
          style={{ color: 'var(--t1)' }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          Loading Solutions
        </motion.div>
        <div className="text-xs tracking-wider" style={{ color: 'var(--t3)' }}>
          Foretek Solutions
        </div>
      </div>
    </div>
  );
};

export default LoaderUi;
